import type { EnergyReconciliation, EnergyHistory } from "@spf/contracts";
import { Failed, Loading } from "../ViewState.js";
import { formatKwh } from "../../lib/energy.js";
import type { Async } from "../../lib/useAsync.js";

export type CheckKind = "discharge" | "solar";

const CHECKS = {
  discharge: { field: "dischargeCheck", measure: "Battery discharge", noun: "battery" },
  solar: { field: "solarCheck", measure: "Solar generation", noun: "solar" },
} as const satisfies Record<
  CheckKind,
  { field: "dischargeCheck" | "solarCheck"; measure: string; noun: string }
>;

/**
 * Whether the energy integrated here agrees with the inverter's own counter,
 * for each of the counters named.
 *
 * A disagreement is worded as the alarm it is. Its likeliest cause is a
 * decoding error, and a decoding error otherwise produces figures that are
 * wrong and entirely believable -- this line is the only place one would show.
 * Solar is held to the same check as discharge because slice 5c's gate is
 * exactly this: derived solar energy agreeing with the solar counter, on
 * screen (docs/DECISIONS/0028).
 */
export function CrossCheck({
  lifetime,
  kinds,
  onRetry,
}: {
  lifetime: Async<EnergyHistory>;
  kinds: readonly CheckKind[];
  onRetry: () => void;
}) {
  if (lifetime.kind === "loading") return <Loading label="Checking against the inverter…" />;
  if (lifetime.kind === "error") return <Failed message={lifetime.message} onRetry={onRetry} />;

  return (
    <div className="flex flex-col gap-3">
      {kinds.map((kind) => (
        <Verdict key={kind} kind={kind} check={lifetime.value[CHECKS[kind].field]} />
      ))}
    </div>
  );
}

function Verdict({ kind, check }: { kind: CheckKind; check: EnergyReconciliation | null }) {
  const { measure, noun } = CHECKS[kind];
  if (check === null) {
    return (
      <p className="text-sm text-ink-muted">
        Not enough readings yet to check this system&rsquo;s {noun} figures against the
        inverter&rsquo;s own counter.
      </p>
    );
  }

  // Over the quarter hours whose neighbour also carried the counter, not the
  // whole lifetime: across a gap the counter keeps counting what was never
  // recorded, so this reads lower than the ledger's lifetime figure.
  const figures = `${measure} over every recorded quarter hour the counter can be compared across: ${formatKwh(check.derivedKwh)} worked out here, ${formatKwh(check.counterKwh)} by the inverter's own counter`;

  if (check.agrees) {
    return (
      <p className="text-sm text-ink-muted">
        <span className="font-semibold text-accent-2-ink">Checked.</span> {figures} &mdash; within
        the {formatKwh(check.toleranceKwh)} allowed.
      </p>
    );
  }

  return (
    <p
      className="rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger-ink"
      role="alert"
    >
      <span className="font-semibold">These figures do not agree.</span> {figures}, a difference of{" "}
      {formatKwh(Math.abs(check.differenceKwh))} against the {formatKwh(check.toleranceKwh)}{" "}
      allowed. The likeliest cause is a decoding error; see &ldquo;Numbers look plausible but
      wrong&rdquo; in the runbook before trusting any energy figure here.
    </p>
  );
}
